import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Badge,
  ProgressBar,
  Alert
} from "react-bootstrap";
import {
  LuPackage,
  LuShoppingCart,
  LuActivity,
  LuTrendingUp,
  LuClock,
  LuDollarSign,
} from "react-icons/lu";
import { BsBarChart } from "react-icons/bs";
import {
  FiBox,
  FiShoppingBag,
  FiActivity,
  FiAlertCircle,
  FiTrendingUp,
  FiClock,
  FiDollarSign,
  FiBarChart
} from "react-icons/fi";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import Useaxios from "utils/Useaxios";
import { API } from "utils/Apiroutes";
import "./Dashboard.css";
import { useNavigate } from "react-router-dom";
import { GRADIENTS } from "utils/theme";
import { COLORS } from "utils/theme";
import { getGlobalCurrency } from "utils/currency";
import { convertPrice } from "utils/currency";


// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const Dashboard = () => {
  const { fetchData } = Useaxios();
  const [dashboardData, setDashboardData] = useState({});
  const [loading, setLoading] = useState(true);
  const [recentOrders, setRecentOrders] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);
  const [lowStockProducts,setLowStockProducts] = useState([]);
  const [expiringProducts,setExpiringProducts] = useState([]);
  const [alertCount, setAlertCount] = useState(0);
  const [revenueView, setRevenueView] = useState('monthly'); // 'daily', 'weekly', 'monthly'
  const [currency, setCurrency] = useState('LRD'); // 'LRD' or 'USD'
  const [categories, setCategories] = useState([]);
  const LRD_TO_USD = 0.0051; // 1 LRD = 0.0051 USD
  const [selectedCurrency, setSelectedCurrency] = useState(getGlobalCurrency());

  // useEffect(()=>{
  //   const timer =setInterval(()=> {
  //    window.location.reload()
  //   }, 20000)

  //   return ()=>clearInterval(timer)
  // },[])

 useEffect(() => {
      const handleCurrencyChange = () => {
        setSelectedCurrency(getGlobalCurrency());
      };
  
      window.addEventListener("currencyChanged", handleCurrencyChange);
  
      return () => {
        window.removeEventListener("currencyChanged", handleCurrencyChange);
      };
    }, []);

  const getDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const [dashRes, transRes, alertRes, catRes] = await Promise.all([
        fetchData({ url: API.dashboard, method: 'GET', showMessage: false }),
        fetchData({ url: API.getTransactions, method: 'GET', showMessage: false }),
        fetchData({ url: API.stockAlert, method: 'GET', showMessage: false }),
        fetchData({ url: API.getCategory, method: 'GET', showMessage: false })
      ]);

      // Process dashboard data
      if (dashRes.success) {
        setDashboardData(dashRes.data);
        if (dashRes.data.recentOrders) {
          setRecentOrders(dashRes.data.recentOrders);
        }
      }

      // Process transactions for revenue
      if (transRes?.success) {
        const transactions = transRes?.data?.data || [];
        const successful = transactions.filter(
          t => t.status?.toUpperCase() === "SUCCESSFUL" || 
               t.status?.toUpperCase() === "SUCCESS" ||
               t.status?.toLowerCase() === "completed"
        );
        
        // Calculate total revenue (all time)
        const totalRevenue = successful.reduce((sum, t) => sum + Number(t.amount || t.totalAmount || 0), 0);
        
        // Calculate today's revenue
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        const todayRevenue = successful
          .filter(t => {
            const tDate = new Date(t.createdAt);
            return tDate >= today && tDate < tomorrow;
          })
          .reduce((sum, t) => sum + Number(t.amount || t.totalAmount || 0), 0);
        
        // Calculate yesterday's revenue for growth
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        
        const yesterdayRevenue = successful
          .filter(t => {
            const tDate = new Date(t.createdAt);
            return tDate >= yesterday && tDate < today;
          })
          .reduce((sum, t) => sum + Number(t.amount || t.totalAmount || 0), 0);
        
        const growth = yesterdayRevenue > 0 
          ? (((todayRevenue - yesterdayRevenue) / yesterdayRevenue) * 100).toFixed(1)
          : 0;
        
        const revenueData = calculateRevenueData(successful);
        setDashboardData(prev => ({ 
          ...prev, 
          ...revenueData,
          revenue: {
            total: totalRevenue,
            today: todayRevenue,
            growth: parseFloat(growth)
          }
        }));
        
        // Set recent orders (top 5 successful transactions)
        setRecentOrders(successful.slice(0, 5));
      }

      // Process alerts
      if (alertRes.success) {
        const unreadCount = alertRes.data?.filter(alert => !alert.isRead).length || 0;
        setAlertCount(unreadCount);
      }

      // Process categories
      if (catRes?.success) {
        const categoryData = catRes.data?.data || [];
        setCategories(categoryData);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateRevenueData = (transactions) => {
    const now = new Date();
    
    // Daily revenue (last 7 days)
    const dailyRevenue = [];
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);
      const nextDate = new Date(date);
      nextDate.setDate(nextDate.getDate() + 1);
      
      const dayRevenue = transactions
        .filter(t => {
          const tDate = new Date(t.createdAt);
          return tDate >= date && tDate < nextDate;
        })
        .reduce((sum, t) => sum + Number(t.amount || t.totalAmount || 0), 0);
      
      dailyRevenue.push({ day: days[date.getDay()], amount: dayRevenue });
    }
    
    // Weekly revenue (last 4 weeks)
    const weeklyRevenue = [];
    for (let i = 3; i >= 0; i--) {
      const weekStart = new Date(now);
      weekStart.setDate(weekStart.getDate() - (i * 7 + 7));
      weekStart.setHours(0, 0, 0, 0);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 7);
      
      const weekRevenue = transactions
        .filter(t => {
          const tDate = new Date(t.createdAt);
          return tDate >= weekStart && tDate < weekEnd;
        })
        .reduce((sum, t) => sum + Number(t.amount || t.totalAmount || 0), 0);
      
      weeklyRevenue.push({ week: `Week ${4 - i}`, amount: weekRevenue });
    }
    
    // Monthly revenue (last 12 months)
    const monthlyRevenue = [];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    for (let i = 11; i >= 0; i--) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const nextMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1);
      
      const monthRevenue = transactions
        .filter(t => {
          const tDate = new Date(t.createdAt);
          return tDate >= monthDate && tDate < nextMonth;
        })
        .reduce((sum, t) => sum + Number(t.amount || t.totalAmount || 0), 0);
      
      monthlyRevenue.push({ month: months[monthDate.getMonth()], amount: monthRevenue });
    }
    
    return { dailyRevenue, weeklyRevenue, monthlyRevenue };
  };

  useEffect(() => {
    getDashboardData();
  }, []);

  // Generate data based on actual dashboard data
  const getRevenueData = () => {
    if (revenueView === 'daily') {
      if (dashboardData.dailyRevenue && Array.isArray(dashboardData.dailyRevenue)) {
        return {
          labels: dashboardData.dailyRevenue.map(item => item.day || ''),
          data: dashboardData.dailyRevenue.map(item => item.amount || 0)
        };
      }
      return { labels: [], data: [] };
    } else if (revenueView === 'weekly') {
      if (dashboardData.weeklyRevenue && Array.isArray(dashboardData.weeklyRevenue)) {
        return {
          labels: dashboardData.weeklyRevenue.map(item => item.week || ''),
          data: dashboardData.weeklyRevenue.map(item => item.amount || 0)
        };
      }
      return { labels: [], data: [] };
    } else {
      if (dashboardData.monthlyRevenue && Array.isArray(dashboardData.monthlyRevenue)) {
        return {
          labels: dashboardData.monthlyRevenue.map(item => item.month || ''),
          data: dashboardData.monthlyRevenue.map(item => item.amount || 0)
        };
      }
      return { labels: [], data: [] };
    }
  };

  const getCategoryDistribution = () => {
    if (categories && Array.isArray(categories) && categories.length > 0) {
      const top5 = categories
        .sort((a, b) => (b.productCount || 0) - (a.productCount || 0))
        .slice(0, 5);
      return {
        labels: top5.map(item => item.name || ''),
        data: top5.map(item => item.productCount || 0)
      };
    }
    return { labels: [], data: [] };
  };

  const getMachinePerformance = () => {
    if (dashboardData.machinePerformance && Array.isArray(dashboardData.machinePerformance)) {
      return {
        labels: dashboardData.machinePerformance.map(item => item.machineName || ''),
        stockData: dashboardData.machinePerformance.map(item => item.stockLevel || 0),
        salesData: dashboardData.machinePerformance.map(item => item.sales || 0)
      };
    }
    // Default empty data
    return {
      labels: [],
      stockData: [],
      salesData: []
    };
  };

  // Prepare chart data based on actual data
  const revenueData = getRevenueData();
  const categoryDistribution = getCategoryDistribution();
  const machinePerformance = getMachinePerformance();

  // Sales Chart Data
  const convertCurrency = (amount) => {
    return currency === 'USD' ? (amount * LRD_TO_USD).toFixed(2) : amount;
  };

  const salesChartData = {
    labels: revenueData.labels,
    datasets: [
      {
        label: `Revenue (in ${currency}) - ${revenueView.charAt(0).toUpperCase() + revenueView.slice(1)}`,
        data: revenueData.data.map(amount => convertCurrency(amount)),
        borderColor: '#667eea',
        backgroundColor: 'rgba(102, 126, 234, 0.1)',
        fill: true,
        tension: 0.4
      }
    ]
  };

  // Product Category Distribution
  const categoryChartData = {
    labels: categoryDistribution.labels,
    datasets: [{
      data: categoryDistribution.data,
      backgroundColor: [
        '#667eea',
        '#8726e8ff',
        '#28a745',
        '#17a2b8',
        '#ffc107',
        '#dc3545',
        '#fd7e14',
        '#6f42c1'
      ],
      borderWidth: 0,
      hoverOffset: 15
    }]
  };

  // Machine Performance Data
  const machineChartData = {
    labels: machinePerformance.labels,
    datasets: [
      {
        label: 'Stock Level (%)',
        data: machinePerformance.stockData,
        backgroundColor: 'rgba(102, 126, 234, 0.6)',
        borderColor: '#667eea',
        borderWidth: 2
      },
      {
        label: 'Sales (LRD)',
        data: machinePerformance.salesData,
        backgroundColor: 'rgba(40, 167, 69, 0.6)',
        borderColor: '#28a745',
        borderWidth: 2
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      x: {
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          boxWidth: 12,
          padding: 8,
          font: {
            size: 11
          }
        }
      }
    },
    cutout: '70%',
    layout: {
      padding: {
        left: 0,
        right: 10,
        top: 10,
        bottom: 10
      }
    }
  };

  // Calculate total stock percentage for progress bar
  const calculateStockPercentage = () => {
    if (!dashboardData.totalStock || !dashboardData.totalProducts) return 0;
    const percentage = (dashboardData.totalStock / (dashboardData.totalProducts * 100)) * 100;
    return Math.min(percentage, 100); // Cap at 100%
  };

  const stockPercentage = calculateStockPercentage();
  const navigate = useNavigate()

  if (loading) {
    return (
      <Container fluid className="py-4">
        <Row>
          <Col md="12">
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted">Loading dashboard...</p>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col md="12">
          <Card className="border-0 shadow">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h2 className="fw-bold mb-1 text-dark">
                    Welcome back, <span className="" style={{
                      color:COLORS.primary
                    }}>Admin</span>! 👋
                  </h2>
                  <p className="text-muted mb-0">
                    {dashboardData.totalMachines ? 
                      `Managing ${dashboardData.totalMachines} machines with ${dashboardData.totalProducts || 0} products` : 
                      ""
                      }
                  </p>
                </div>

                <div className="d-flex gap-2">
                  <Button 
                    variant="outline-danger" 
                    style={{background: loading ? '#6c757d' : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      border:"none", color:"white"}}
                    size="sm"
                    onClick={()=>{
                      console.log("clicked");
                      navigate('/admin/dashboard/alerts')
                    }}
                    className="position-relative"
                  >
                    <FiAlertCircle className="me-2" />
                    Alert
                    {alertCount > 0 && (
                      <Badge 
                        bg="danger" 
                        pill 
                        className="position-absolute top-0 start-100 translate-middle"
                        style={{ fontSize: '0.65rem' }}
                      >
                        {alertCount}
                      </Badge>
                    )}
                  </Button>
                  <Button 
                    variant="outline-primary" 
                    style={{background: loading ? '#6c757d' : '#17AAA7', color:"white", border:"none"}}
                    size="sm"
                    onClick={getDashboardData}
                    disabled={loading}
                  >
                    <LuActivity className="me-2" />
                    {loading ? 'Refreshing...' : 'Refresh Data'}
                  </Button>
                  {/* <Button variant="primary" size="sm">
                    <LuTrendingUp className="me-2" />
                    View Report
                  </Button> */}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Stats Cards */}
      <Row className="g-4 mb-4">
        {/* Machines Card */}
        <Col xl={3} lg={6} md={6}>
          <Card className="border-0 shadow stat-card hover-lift" style={{ cursor: 'pointer' }} onClick={()=>navigate('/admin/machines/list')}>
            <Card.Body className="p-3" style={{ position: 'relative' }}>
              <div
            
               className="stat-icon  bg-primary bg-opacity-10 text-primary rounded-circle p-2 d-flex align-items-center justify-content-center" 
               style={{ width: '48px', height: '48px', position: 'absolute', top: '12px', right: '-5px' }}>
                <FiBox size={20} />
              </div>
              <h6 className="text-muted mb-2 fw-medium" style={{ fontSize: '0.8rem' }}>Total Machines</h6>
              <h2 className="fw-bold text-dark mb-2" style={{ fontSize: '2rem' }}>
                {dashboardData?.machines?.total || 0}
              </h2>
              <div className="d-flex align-items-center gap-2">
                <Badge bg="success" style={{ cursor: 'pointer', fontSize: '0.7rem', padding: '0.25rem 0.5rem' }} onClick={(e)=>{ e.stopPropagation(); navigate('/admin/machines/list?status=online'); }}>
                  {dashboardData?.machines?.online || 0} Online
                </Badge>
                <Badge bg="danger" style={{ cursor: 'pointer', fontSize: '0.7rem', padding: '0.25rem 0.5rem' }} onClick={(e)=>{ e.stopPropagation(); navigate('/admin/machines/list?status=offline'); }}>
                  {dashboardData?.machines?.offline || 0} Offline
                </Badge>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Categories Card */}
        <Col xl={3} lg={6} md={6}>
          <Card className="border-0 shadow stat-card hover-lift" style={{ cursor: 'pointer' }} onClick={()=>navigate('/admin/product/categories')}>
            <Card.Body className="p-3" style={{ position: 'relative' }}>
              <div className="stat-icon bg-info bg-opacity-10 text-info rounded-circle p-2 d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', position: 'absolute', top: '12px', right: '12px' }}>
                <LuPackage size={20} />
              </div>
              <h6 className="text-muted mb-2 fw-medium" style={{ fontSize: '0.8rem' }}>Categories</h6>
              <h2 className="fw-bold text-dark mb-0" style={{ fontSize: '2rem' }}>
                {dashboardData?.categories || 0}
              </h2>
            </Card.Body>
          </Card>
        </Col>

        {/* Products Card */}
        <Col xl={3} lg={6} md={6}>
          <Card className="border-0 shadow stat-card hover-lift" style={{ cursor: 'pointer' }} onClick={()=>navigate('/admin/product/list')}>
            <Card.Body className="p-3" style={{ position: 'relative' }}>
              <div className="stat-icon bg-success bg-opacity-10 text-success rounded-circle p-2 d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', position: 'absolute', top: '12px', right: '12px' }}>
                <FiShoppingBag size={20} />
              </div>
              <h6 className="text-muted mb-2 fw-medium" style={{ fontSize: '0.8rem' }}>Products</h6>
              <h2 className="fw-bold text-dark mb-0" style={{ fontSize: '2rem' }}>
                {dashboardData?.products || 0}
              </h2>
            </Card.Body>
          </Card>
        </Col>

        {/* Revenue Card */}
        <Col xl={3} lg={6} md={6}>
          <Card className="border-0 shadow stat-card hover-lift">
            <Card.Body className="p-3" style={{ position: 'relative' }}>
              <div
               className="stat-icon bg-warning bg-opacity-10 text-warning rounded-circle p-2 d-flex align-items-center justify-content-center" 
               style={{ width: '48px', height: '48px', position: 'absolute', top: '12px', right: '-10px' }}>
                <FiDollarSign size={20} />
              </div>
              <h6 className="text-muted mb-2 fw-medium" style={{ fontSize: '0.8rem' }}>Total Revenue</h6>
              <h2 className="fw-bold text-dark mb-1" style={{ fontSize: '1.5rem' }}>
                {dashboardData?.revenue?.total?.toLocaleString() || "0"} LRD
              </h2>
              <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>
                ≈ ${((dashboardData?.revenue?.total || 0) * LRD_TO_USD).toFixed(2)} USD
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Charts Row */}
      <Row className="g-4 mb-4">
        {/* Sales Chart */}
        <Col xl={8} lg={7}>
          <Card className="border-0 shadow h-100">
            <Card.Header className="bg-white border-0 py-3 px-4">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="fw-semibold mb-0">
                  <BsBarChart className="me-2 text-primary" />
                  Revenue Overview
                </h5>
                <div className="d-flex gap-2 align-items-center">
                  <div className="d-flex gap-2">
                    <Button 
                      variant={revenueView === 'monthly' ? 'primary' : 'outline-secondary'} 
                      size="sm"
                      onClick={() => setRevenueView('monthly')}
                    >
                      Monthly
                    </Button>
                    <Button 
                      variant={revenueView === 'weekly' ? 'primary' : 'outline-secondary'} 
                      size="sm"
                      onClick={() => setRevenueView('weekly')}
                    >
                      Weekly
                    </Button>
                    <Button 
                      variant={revenueView === 'daily' ? 'primary' : 'outline-secondary'} 
                      size="sm"
                      onClick={() => setRevenueView('daily')}
                    >
                      Daily
                    </Button>
                  </div>
                  <div className="vr" style={{ height: '24px' }}></div>
                  <div className="d-flex gap-2">
                    <Button 
                      variant={currency === 'LRD' ? 'success' : 'outline-success'} 
                      size="sm"
                      onClick={() => setCurrency('LRD')}
                    >
                      LRD
                    </Button>
                    <Button 
                      variant={currency === 'USD' ? 'success' : 'outline-success'} 
                      size="sm"
                      onClick={() => setCurrency('USD')}
                    >
                      USD
                    </Button>
                  </div>
                </div>
              </div>
            </Card.Header>
            <Card.Body className="p-4 d-flex align-items-center justify-content-center">
              {revenueData.data.length > 0 ? (
                <div style={{ height: '300px', width: '100%' }}>
                  <Line data={salesChartData} options={chartOptions} />
                </div>
              ) : (
                <div className="text-center py-5">
                  <div className="display-4 text-muted mb-3">
                    <FiBarChart />
                  </div>
                  <h5 className="text-muted mb-2">No {revenueView.charAt(0).toUpperCase() + revenueView.slice(1)} Revenue Data</h5>
                  <p className="text-muted mb-0">
                    {revenueView.charAt(0).toUpperCase() + revenueView.slice(1)} revenue data will appear here once transactions are recorded
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Category Distribution */}
        <Col xl={4} lg={5}>
          <Card className="border-0 shadow h-100">
            <Card.Header className="bg-white border-0 py-3 px-4">
              <h5 className="fw-semibold mb-0">
                <FiBarChart className="me-2 text-primary" />
                Top 5 Categories
              </h5>
              <small className="text-muted">By product count</small>
            </Card.Header>
            <Card.Body className="p-4">
              {categoryDistribution.data.length > 0 ? (
                <div className="d-flex flex-column" style={{ height: '100%' }}>
                  <div style={{ height: '220px', flexShrink: 0 }}>
                    <Doughnut data={categoryChartData} options={doughnutOptions} />
                  </div>
                  <div className="mt-3 flex-grow-1 overflow-auto">
                    {categories.slice(0, 5).sort((a, b) => (b.productCount || 0) - (a.productCount || 0)).map((cat, idx) => (
                      <div key={idx} className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                        <div className="d-flex align-items-center gap-2">
                          <div 
                            className="rounded-circle" 
                            style={{ 
                              width: '12px', 
                              height: '12px', 
                              backgroundColor: ['#667eea', '#8726e8ff', '#28a745', '#17a2b8', '#ffc107'][idx] 
                            }}
                          ></div>
                          <div>
                            <span className="small fw-semibold d-block">{cat.name}</span>
                       
                          </div>
                        </div>
                        <Badge bg="primary" className="rounded-pill">{cat.productCount || 0}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-5">
                  <div className="display-4 text-muted mb-3">
                    <LuPackage />
                  </div>
                  <h5 className="text-muted mb-2">No Categories</h5>
                  <p className="text-muted mb-0">
                    Category distribution will appear here once products are added
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Recent Sales & Machine Status */}
      <Row className="g-4">
        {/* Recent Orders */}
        <Col xl={8} lg={7}>
          <Card className="border-0 shadow">
            <Card.Header className="bg-white border-0 py-3 px-4">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="fw-semibold mb-0">
                  <LuShoppingCart className="me-2 text-primary" />
                  Recent Orders
                </h5>
                {recentOrders.length > 0 && (
                  <Button onClick={()=>navigate('/admin/sales-report')}variant="link" className="text-decoration-none p-0">
                    View All
                  </Button>
                )}

                
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {recentOrders.length > 0 ? (
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="ps-4" style={{width:"30%" ,}}>Order ID</th>
                        <th>Product</th>
                        <th>Machine</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th className="pe-4">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentOrders.map((order) => (
                        <tr key={order._id}>
                          <td className="ps-4">
                            <span className="fw-semibold">{order.transactionId}</span>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="product-icon bg-light rounded-circle p-2 me-3">
                                <LuPackage size={14} />
                              </div>
                              <span>{order.items?.map(item => item.productId?.name).filter(Boolean).join(", ") || 'N/A'}</span>
                            </div>
                          </td>
                          <td>
                            <Badge bg="light" text="dark" className="border">
                              {order.machineId || 'N/A'}
                            </Badge>
                          </td>
                          <td>
                            <span className="fw-semibold">{getGlobalCurrency()}{convertPrice(order.amount || order.totalAmount || 0 , 'LRD').toFixed(2)}</span>
                          </td>
                          <td>
                            <Badge bg="success" className="px-3 py-1">
                              {order.status}
                            </Badge>
                          </td>
                          <td className="pe-4">
                            <div className="d-flex align-items-center">
                               <div>
                                    <span className="fw-medium">{new Date(order.createdAt).toLocaleDateString()}</span>
                                  </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-5">
                  <div className="display-4 text-muted mb-3">
                    <LuShoppingCart />
                  </div>
                  <h5 className="text-muted mb-2">No Recent Orders</h5>
                  <p className="text-muted mb-0">
                    Recent orders will appear here once customers make purchases
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Machine Status */}
        <Col xl={4} lg={5}>
          <Card className="border-0 shadow">
            <Card.Header className="bg-white border-0 py-3 px-4">
              <h5 className="fw-semibold mb-0">
                <FiActivity className="me-2 text-primary" />
                Machine Status
              </h5>
            </Card.Header>
            <Card.Body className="p-4">
              {machinePerformance.labels.length > 0 ? (
                <>
                  <div style={{ height: '250px' }}>
                    <Bar data={machineChartData} options={chartOptions} />
                  </div>
                  <div className="mt-4">
                    <h6 className="fw-semibold mb-3">Quick Stats</h6>
                    <div className="row g-2">
                      <div className="col-6">
                        <div className="bg-light rounded-3 p-3 text-center">
                          <div className="text-primary fw-bold fs-4">
                            {dashboardData?.machines?.online || 0}
                          </div>
                          <small className="text-muted">Online Machines</small>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="bg-light rounded-3 p-3 text-center">
                          <div className="text-danger fw-bold fs-4">
                            {dashboardData?.machines?.offline || 0}
                          </div>
                          <small className="text-muted">Offline Machines</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {(dashboardData?.machines?.online || 0) + (dashboardData?.machines?.offline || 0) > 0 ? (
                    <div className="mt-2">
                      <h6 className="fw-semibold mb-3">Quick Stats</h6>
                      <div className="row g-2">
                        <div className="col-6">
                          <div className="bg-light rounded-3 p-3 text-center">
                            <div className="text-primary fw-bold fs-4">
                              {dashboardData?.machines?.online || 0}
                            </div>
                            <small className="text-muted">Online Machines</small>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-light rounded-3 p-3 text-center">
                            <div className="text-danger fw-bold fs-4">
                              {dashboardData?.machines?.offline || 0}
                            </div>
                            <small className="text-muted">Offline Machines</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <div className="display-4 text-muted mb-3">
                        <FiActivity />
                      </div>
                      <h5 className="text-muted mb-2">No Machine Data</h5>
                      <p className="text-muted mb-0">
                        Machine performance data will appear here once machines are active
                      </p>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

     
    </Container>
  );
};

export default Dashboard;